import { FC } from 'react';
import styled, { AnyStyledComponent } from 'styled-components';
import {
  RailProps,
  RailHotspotProps,
  DotProps,
  DotStyle,
  TrackProps,
  TrackStyle,
  TrackHotspotProps,
  TickValueProps,
  TickIndicatorProps,
} from './slider.types';
import COLORS from '../../style/universal.color';

const sliderSizes = {
  railSize: 3,
  dotSize: 20,
  trackSize: 5,
  touchArea: 40,
}

const sliderColors: object = {
  primary: {
    track: COLORS.linkBlueOnDark,
    rail: COLORS.mlbGrey3,
    dot: COLORS.mlbWhite,
    dotShadow: COLORS.linkBlueOnDarkShadow,
    tick: COLORS.mlbGrey3,
  },
  dark: {
    track: COLORS.linkBlueOnDark,
    rail: COLORS.mlbGrey5,
    dot: COLORS.mlbWhite,
    dotShadow: COLORS.mlbWhiteShadow,
    tick: COLORS.mlbGrey5,
  }
}

const retrieveColorTheme = (style: string, prop: string): string => {
  return sliderColors[style][prop];
}

export const Rail: FC<RailProps> = styled.div`
    background-color: ${({ theme }: RailProps) => retrieveColorTheme(theme, 'rail')};
    position: absolute;
    pointer-events: none;
    border-radius: 1.5px;
    ${({ vertical }: RailProps) => vertical ? `
        height: 100%;
        width: ${sliderSizes.railSize}px;
        transform: translate(-50%, 0%);
    ` : `
        width: 100%;
        height: ${sliderSizes.railSize}px;
        transform: translate(0%, -50%);
    `}
`

export const RailHotspot: FC<RailHotspotProps> = styled.div`
    cursor: pointer;
    position: absolute;
    ${({ vertical }: RailHotspotProps) => vertical ? `
        height: 100%;
        width: ${sliderSizes.dotSize * 2}px;
        transform: translate(-50%, 0%);
    ` : `
        width: 100%;
        height: ${sliderSizes.dotSize * 2}px;
        transform: translate(0%, -50%);
    `}
`

const getDotAttrs = ({ vertical, left }: DotProps) => {
  const style: DotStyle = {};
  if (vertical) {
    style.top = left;
  } else {
    style.left = left;
  }
  return {
    style
  };
};

export const Dot: AnyStyledComponent = styled.button.attrs(getDotAttrs)`
    background-color: ${({ theme }: DotProps) => retrieveColorTheme(theme, 'dot')};
    width: ${sliderSizes.dotSize}px;
    height: ${sliderSizes.dotSize}px;
    padding: 0;
    border: 0;
    border-radius: 50%;
    white-space: nowrap;
    position: absolute;
    z-index: 2;
    cursor: pointer;
    border: solid 1px ${COLORS.mlbGrey4};
    transform: translate(-50%, -50%);

    ${({ isActive, theme }: DotProps) => isActive && (
      `box-shadow: 0px 0px 1px 10px ${retrieveColorTheme(theme, 'dotShadow')};`
    )}

    @media (pointer: coarse) {
        &:before {
            content: "";
            display: block;
            width: ${sliderSizes.touchArea}px;
            height: ${sliderSizes.touchArea}px;
            transform: translate(-${(sliderSizes.touchArea - sliderSizes.dotSize) / 2}px, -${(sliderSizes.touchArea - sliderSizes.dotSize) / 2}px);
        }
    }
`;

const getTrackAttrs = ({ vertical, left, width }: TrackProps) => {
  const style: TrackStyle = {};
  if (vertical) {
    style.top = left;
    style.height = width;
  } else {
    style.left = left;
    style.width = width;
  }
  return {
    style
  };
};

export const Track: AnyStyledComponent = styled.div.attrs(getTrackAttrs)`
    background-color: ${({ theme }: TrackProps) => retrieveColorTheme(theme, 'track')};
    position: absolute;
    z-index: 1;
    pointer-events: none;
    border-radius: 2.5px;
    ${({ vertical }: TrackProps) => vertical ? `
        width: ${sliderSizes.trackSize}px;
        transform: translate(-50%, 0%);
        ` : `
        height: ${sliderSizes.trackSize}px;
        transform: translate(0%, -50%);
    `}
`;

export const TrackHotspot: AnyStyledComponent = styled.div.attrs(getTrackAttrs)`
    position: absolute;
    cursor: pointer;
    ${({ vertical }: TrackHotspotProps) => vertical ? `
        width: ${sliderSizes.dotSize * 2}px;
        transform: translate(-50%, 0%);
        ` : `
        height: ${sliderSizes.dotSize * 2}px;
        transform: translate(0%, -50%);
    `}
`;

export const TickIndicator: FC<TickIndicatorProps> = styled.div<TickIndicatorProps>`
    position: absolute;
    background-color: ${({ theme }: TickIndicatorProps) => retrieveColorTheme(theme, 'tick')};
    ${({ vertical, tickPercent }: TickIndicatorProps) => vertical ? `
        top: ${tickPercent}%;
        width: 6px;
        height: 1px;
        margin-top: -0.5px;
        margin-left: 10px;
        ` : `
        left: ${tickPercent}%;
        width: 1px;
        height: 10px;
        margin-top: 14px;
    `}
`;

export const TickValue: FC<TickValueProps> = styled.div<TickValueProps>`
    position: absolute;
    font-size: 10px;
    color: ${({ theme }: TickIndicatorProps) => retrieveColorTheme(theme, 'tick')};
    ${({ vertical, tickPercent, count }: TickValueProps) => vertical ? `
        top: ${tickPercent}%;
        margin-top: -5px;
        margin-left: 20px;
        ` : `
        left: ${tickPercent}%;
        width: ${100 / count}%;
        margin-left: ${-(100 / count) / 2}%;
        margin-top: 22px;
        text-align: center;
    `}
`;
